import i18n from "../../../i18n";
import { IAppointment, ViewType } from "../types/appointments.types";

/**
 * Builds the payload for the AppointmentsCalendar export (PDF/Excel) from the
 * appointments currently displayed in the client calendar.
 *
 * The backend groups rows by the "Date" column for PDF, so rows are returned
 * pre-sorted by date+time. Headers are sent as i18n KEYS — the backend resolves
 * them per language.
 */

const tr = (key: string, fallback: string) =>
  i18n.t(key, { defaultValue: fallback }) as string;

const DAY_KEYS = [
  "APPOINTMENTS_DAY_SUNDAY",
  "APPOINTMENTS_DAY_MONDAY",
  "APPOINTMENTS_DAY_TUESDAY",
  "APPOINTMENTS_DAY_WEDNESDAY",
  "APPOINTMENTS_DAY_THURSDAY",
  "APPOINTMENTS_DAY_FRIDAY",
  "APPOINTMENTS_DAY_SATURDAY",
];
const DAY_FALLBACK = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

const VIEW_LABEL: Record<ViewType, { key: string; fallback: string }> = {
  daily: { key: "APPOINTMENTS_VIEW_DAILY", fallback: "יומי" },
  weekly: { key: "APPOINTMENTS_VIEW_WEEKLY", fallback: "שבועי" },
  monthly: { key: "APPOINTMENTS_VIEW_MONTHLY", fallback: "חודשי" },
};

const pad = (n: number) => n.toString().padStart(2, "0");

const toDate = (iso: string): Date | null => {
  if (!iso) return null;
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (iso: string): string => {
  const d = toDate(iso);
  if (!d) return iso || "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const dayName = (iso: string): string => {
  const d = toDate(iso);
  if (!d) return "—";
  const idx = d.getDay();
  return tr(DAY_KEYS[idx], DAY_FALLBACK[idx]);
};

const computeEndTime = (time?: string, duration?: number): string => {
  if (!time || !duration) return "—";
  const [h, m] = time.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return "—";
  const total = h * 60 + m + duration;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
};

const dash = (v?: string | number | null): string => {
  if (v === undefined || v === null) return "—";
  const s = String(v).trim();
  return s === "" ? "—" : s;
};

const treatmentCell = (a: IAppointment): string =>
  dash(a.treatmentTypeName || a.treatmentProgramName || a.treatmentGroupName);

export interface BuildAppointmentsExportOptions {
  viewType: ViewType;
  rangeLabel: string;
  filterSummary?: string[];
}

export interface AppointmentsExportPayload {
  entity: string;
  columns: string[];
  headers: string[];
  dataRows: Record<string, any>[];
  title: string;
  subtitle: string;
  filterSummary?: string[];
}

export function buildAppointmentsExport(
  appointments: IAppointment[],
  { viewType, rangeLabel, filterSummary }: BuildAppointmentsExportOptions
): AppointmentsExportPayload {
  const columns = ["Date", "Day", "Time", "EndTime", "Duration", "Therapist", "Treatment"];
  const headers = [
    "DATE",
    "DAY",
    "APPOINTMENTS_TIME",
    "END_TIME",
    "DURATION",
    "THERAPIST_NAME",
    "TREATMENT_TYPE",
  ];

  const minutesLabel = tr("MINUTES_SHORT", "דק׳");

  const sorted = [...appointments].sort((a, b) => {
    const d = (a.date || "").localeCompare(b.date || "");
    if (d !== 0) return d;
    return (a.time || "").localeCompare(b.time || "");
  });

  const dataRows = sorted.map((a) => ({
    Date: formatDate(a.date),
    Day: dayName(a.date),
    Time: dash(a.time),
    EndTime: a.endTime ? dash(a.endTime) : computeEndTime(a.time, a.duration),
    Duration: a.duration ? `${a.duration} ${minutesLabel}` : "—",
    Therapist: dash(a.therapistName),
    Treatment: treatmentCell(a),
  }));

  const viewLabelConf = VIEW_LABEL[viewType];
  const viewLabel = tr(viewLabelConf.key, viewLabelConf.fallback);
  const subtitle = `${tr("VIEW", "תצוגה")}: ${viewLabel} · ${rangeLabel}`;

  return {
    entity: "AppointmentsCalendar",
    columns,
    headers,
    dataRows,
    title: tr("APPOINTMENTS_CALENDAR_EXPORT_TITLE", "יומן תורים"),
    subtitle,
    filterSummary,
  };
}

/** Builds the active-filter chips for the client calendar (therapist search only). */
export function buildSearchFilterSummary(searchTerm?: string): string[] {
  const chips: string[] = [];
  if (searchTerm && searchTerm.trim()) {
    chips.push(`${tr("SEARCH", "חיפוש")}: ${searchTerm.trim()}`);
  }
  return chips;
}
